/// useVariableWatch: React hook for managing pinned variable watches.
/// Reads from debugStore's pinnedWatches with resolved values.
///
/// Phase 20, Plan 03.

import { useCallback } from "react";
import { useDebugStore } from "@/stores/debugStore";
import type { WatchEntry } from "@/types/debug";

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Hook for pinned variable watch management.
 * Provides pin/unpin and reactive access to watch values from debugStore.
 *
 * Values are resolved automatically when debugStore receives stepCompleted
 * or breakpointHit events (via handleDebugEvent -> updateWatchValues).
 */
export function useVariableWatch() {
  const watches: WatchEntry[] = useDebugStore((s) => s.pinnedWatches);

  /** Pin a variable path to the watch list. */
  const pinWatch = useCallback((variablePath: string) => {
    useDebugStore.getState().pinWatch(variablePath);
  }, []);

  /** Unpin a variable path from the watch list. */
  const unpinWatch = useCallback((variablePath: string) => {
    useDebugStore.getState().unpinWatch(variablePath);
  }, []);

  return { watches, pinWatch, unpinWatch };
}
